import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HEXColor, TailwindColor } from 'libs/shared/src/models';
import { ModelType } from 'libs/shared/src/models/search/search-base-item';
import { ITeaser } from 'libs/shared/src/models/teaser/teaser';
import { ColorService, isHexColor, TeaserService } from 'libs/shared/src/services';

@Component({
  selector: 'shared-teaser-badge',
  imports: [TranslatePipe, CommonModule],
  templateUrl: './shared-teaser-badge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class SharedTeaserBadgeComponent {
  // Signals
  /** Type of the item */
  itemType = input<ModelType | undefined>();
  /** The color associated with the item type (e.g., 'primary-main') */
  itemTypeColor = input<TailwindColor | undefined>();
  /** The teaser for the article — either a plain label or a full teaser object (linked events deliver an `ITeaser`) */
  articleTeaser = input<string | ITeaser | undefined>();
  /** The color associated with the article teaser (in HEX format) */
  articleTeaserColor = input<string | undefined>();
  /** The creation date of the article */
  articleCreatedOn = input<Date | string | undefined>();
  /** Number of days the article is considered "new" */
  newForDays = input<number | undefined>();
  /** Flag to show or hide the item type badge */
  showItemTypeBadge = input<boolean>(false);
  /** Flag to show or hide the date badge (used by News/CalendarItem to show validFrom/validTo as a teaser) */
  showDateBadge = input<boolean>(false);
  /** Pre-formatted date label to display inside the date badge */
  dateBadgeLabel = input<string | undefined>(undefined);
  /** Flag to show or hide the available status badge */
  showAvailableStatusBadge = input<boolean>(false);
  /** Flag to show or hide the discount badge */
  showDiscountBadge = input<boolean>(false);
  /** Flag to show or hide the article teaser badge */
  showArticleTeaserBadge = input<boolean>(false);
  /** The current price information of the article */
  currentPrice = input<number | undefined>();
  /** The old price information of the article */
  oldPrice = input<number | undefined>();
  /** Indicates whether the badge should be positioned absolutely */
  isBadgeAbsolute = input<boolean>(false);
  /** Indicates whether the article is available */
  isAvailableArticle = input<boolean>(false);
  /** The currency of the article prices */
  currency = input<string | undefined>();
  /** Badge size variant — 'sm' for compact contexts (e.g. typeahead), 'md' for gallery cards (default) */
  size = input<'sm' | 'md'>('md');

  // Services
  protected readonly teaserService = inject(TeaserService);
  private readonly colorService = inject(ColorService);
  private readonly translate: TranslateService = inject(TranslateService);

  // Computed
  /**
   * Resolves the teaser to its display label.
   * Products deliver the teaser as a plain string, while linked events deliver a full `ITeaser` object.
   * @returns The teaser label, or undefined when no teaser (or no name) is set.
   */
  teaserName = computed<string | undefined>(() => {
    const teaser = this.articleTeaser();
    if (!teaser) return undefined;
    return typeof teaser === 'string' ? teaser : teaser.name;
  });
  /**
   * Resolves the teaser color, preferring the explicit color input and falling back to the
   * color carried on an `ITeaser` object.
   * @returns The HEX color string, or undefined when none is available.
   */
  teaserColorValue = computed<string | undefined>(() => {
    const explicit = this.articleTeaserColor();
    if (explicit) return explicit;
    const teaser = this.articleTeaser();
    return teaser && typeof teaser !== 'string' ? teaser.color : undefined;
  });
  /** Computes the CSS classes for the item type badge based on the item type color. */
  itemTypeColorClass = computed(() => {
    const color = this.itemTypeColor() ?? 'primary-main';
    const bgColorClass = `bg-${color}`;
    const textColorClass = `text-${this.colorService.getContrastColorOf(color)}`;
    return `${bgColorClass} ${textColorClass}`;
  });
  /** Computes the base padding/sizing class for all badges based on the size input. */
  badgeBaseClass = computed(() => (this.size() === 'sm' ? 'w-fit px-2 py-1' : 'w-fit px-8 py-3.5 md:px-5 md:py-4'));
  /**
   * Determines whether the article is considered "new".
   * @returns {boolean} True if the OCS "new" teaser key is set, or the article creation date
   * lies within the "new for days" window.
   */
  isNewArticle = computed(() => {
    return this.teaserService.isNewArticle(this.articleCreatedOn(), this.newForDays(), this.articleTeaser());
  });
  /**
   * Determines whether the discount badge should be displayed.
   * @returns {boolean} true if an old price exists and is greater than the current price, otherwise false.
   */
  isDiscountBadge = computed(() => {
    const price = this.currentPrice();
    const priceOld = this.oldPrice();
    return !!price && !!priceOld && priceOld > price;
  });
  /**
   * Determines whether any badge should be displayed.
   * @returns {boolean} true if a discount badge, a selected key teaser,
   * an article teaser, or the article is considered "new", otherwise false.
   */
  isBadgesShown = computed(() => {
    return (
      this.isDiscountBadge() ||
      !!this.teaserName() ||
      this.isNewArticle() ||
      (this.showItemTypeBadge() && !!this.itemType()) ||
      (this.showDateBadge() && !!this.dateBadgeLabel())
    );
  });
  /**
   * Calculates the value to be displayed inside the badge.
   * @returns {string | number | undefined}
   */
  badgeValue = computed(() => {
    const price = this.currentPrice();
    const priceOld = this.oldPrice();
    if (!price) return undefined;

    if (this.isBadgeAbsolute()) {
      return (priceOld ?? 0) - price;
    } else {
      const discountPercent = Math.round(100 - (price / (priceOld ?? price)) * 100);
      return `${discountPercent}%`;
    }
  });
  /**
   * Calculates the price difference between the old and current price.
   * @returns {number | undefined} The calculated price difference, or undefined if values are missing.
   */
  priceDifference = computed(() => {
    const price = this.currentPrice();
    const priceOld = this.oldPrice();
    if (!price || !priceOld) return undefined;

    const diff = priceOld - price;
    return Number(diff.toFixed(2));
  });

  // Methods
  /**
   * Generates a color configuration for the teaser.
   * @param {string} [color] - The main color in HEX format.
   * @returns {{ main: HEXColor; contrast: HEXColor } | undefined}
   * An object containing the main color and its calculated contrast color,
   * or undefined if the input is not a valid HEX color.
   */
  teaserColors(color?: string): { main: HEXColor; contrast: HEXColor } | undefined {
    if (color && isHexColor(color)) {
      return {
        main: color,
        contrast: ColorService.getContrastColorOf(color)
      };
    }
    return undefined;
  }

  /**
   * Returns the translated teaser label based on the given item type.
   * Supports all ModelType variants (Article, Page, Report, Video, Category, Rating, TeamMember, CalendarItem).
   * @param itemType - The type of the item.
   * @returns The translated badge label as string.
   */
  public getTeaserBadgeLabel(itemType?: ModelType): string {
    if (!itemType) return '';

    switch (itemType) {
      case 'Page':
        return this.translate.instant('components.searchGallery.shared.page-item.page');
      case 'Report':
        return this.translate.instant('components.searchGallery.shared.report-item.report');
      case 'Video':
        return this.translate.instant('components.searchGallery.shared.video-item.video');
      case 'Category':
        return this.translate.instant('components.searchGallery.shared.category-item.category');
      case 'Rating':
        return this.translate.instant('components.searchGallery.shared.rating-item.rating');
      case 'TeamMember':
        return this.translate.instant('components.searchGallery.shared.team-item.teamMember');
      case 'CalendarItem':
        return this.translate.instant('components.searchGallery.shared.calendar-item.calendar');
      case 'News':
        return this.translate.instant('components.searchGallery.shared.news-item.news');
      case 'ChainStore':
        return this.translate.instant('components.searchGallery.shared.chain-store-item.chain-store');
      case 'Partner':
        return this.translate.instant('components.searchGallery.shared.partner-item.partner');
      case 'Leaflet':
        return this.translate.instant('components.searchGallery.shared.leaflet-item.leaflet');
      case 'Event':
        return this.translate.instant('components.searchGallery.shared.event-item.event');
      case 'Timeline':
        return this.translate.instant('components.searchGallery.shared.timeline-item.timeline');
      case 'Article':
        return this.translate.instant('components.searchGallery.shared.product-item.product');
      default:
        return this.translate.instant('components.searchGallery.shared.default-item.default');
    }
  }
}
